import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../AuthContext";
const UserRestrictionsManagement = () => {
    const { token } = useContext(AuthContext);


    const [users, setUsers] = useState(null);
    const [roles, setRoles] = useState(null);
    const [projects, setProjects] = useState(null);
    const [userRolesMap, setUserRolesMap] = useState({});
    const [userProjectsMap, setUserProjectsMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [activeView, setActiveView] = useState("addUser");
    const [newName, setNewName] = useState("");
    const [newEmail, setNewEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [creatingUser, setCreatingUser] = useState(false);

    useEffect(() => {
        if (!token) return;

        setLoading(true);
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        };

        const fetchJson = async (url) => {
            const res = await fetch(url, { headers });
            if (!res.ok) {
                throw new Error(`GET ${url} failed with status ${res.status}`);
            }
            return res.json();
        };

        Promise.all([
            // 2a) Fetch all users
            fetchJson(`${process.env.REACT_APP_API_URL}/users`).then((data) => {
                if (Array.isArray(data)) {
                    setUsers(data);
                } else {
                    console.error("Expected /users → array, got:", data);
                    setUsers([]);
                }
            }),

            // 2b) Fetch all roles
            fetchJson(`${process.env.REACT_APP_API_URL}/roles`).then((data) => {
                if (Array.isArray(data)) {
                    setRoles(data);
                } else {
                    console.error("Expected /roles → array, got:", data);
                    setRoles([]);
                }
            }),

            // 2c) Fetch all projects
            fetchJson(`${process.env.REACT_APP_API_URL}/projects`).then((data) => {
                // /projects returns { success:true, projects:[ … ] }
                if (data.success && Array.isArray(data.projects)) {
                    setProjects(data.projects);
                } else {
                    console.error("Expected /projects → { success:true, projects:[…] }, got:", data);
                    setProjects([]);
                }
            }),

            // 2d) Fetch existing user→role mappings
            fetchJson(`${process.env.REACT_APP_API_URL}/user-roles`).then((data) => {
                if (data && typeof data === "object" && !Array.isArray(data)) {
                    setUserRolesMap(data);
                } else {
                    console.error("Expected /user-roles → object, got:", data);
                    setUserRolesMap({});
                }
            }),

            // 2e) Fetch existing user→project mappings
            fetchJson(`${process.env.REACT_APP_API_URL}/user-projects`).then((data) => {
                if (data && typeof data === "object" && !Array.isArray(data)) {
                    setUserProjectsMap(data);
                } else {
                    console.error("Expected /user-projects → object, got:", data);
                    setUserProjectsMap({});
                }
            }),
        ])
            .catch((err) => {
                console.error("Error during initial data fetch:", err);
                setUsers((u) => (u === null ? [] : u));
                setRoles((r) => (r === null ? [] : r));
                setProjects((p) => (p === null ? [] : p));
                setUserRolesMap((m) => (Object.keys(m).length === 0 ? {} : m));
                setUserProjectsMap((m) => (Object.keys(m).length === 0 ? {} : m));
            })
            .finally(() => {
                setLoading(false);
            });
    }, [token]);


 
    if (loading || users === null || roles === null || projects === null) {
        return (
            <div className="flex items-center justify-center h-64">
                <span className="text-gray-600">Loading…</span>
            </div>
        );
    }

    const toggleRoleForUser = (userId, roleId) => {
        setUserRolesMap((prev) => {
            const arr = prev[userId] || [];
            const has = arr.includes(roleId);
            const updated = has ? arr.filter((x) => x !== roleId) : [...arr, roleId];
            return { ...prev, [userId]: updated };
        });
    };


 
    const toggleProjectForUser = (userId, projectId) => {
        setUserProjectsMap((prev) => {
            const arr = prev[userId] || [];
            const has = arr.includes(projectId);
            const updated = has ? arr.filter((x) => x !== projectId) : [...arr, projectId];
            return { ...prev, [userId]: updated };
        });
    };
    const toggleAllRolesForUser = (userId) => {
        const curr = userRolesMap[userId] || [];
        if (curr.length < roles.length) {
            setUserRolesMap((prev) => ({ ...prev, [userId]: roles.map((r) => r.id) }));
        } else {
            setUserRolesMap((prev) => ({ ...prev, [userId]: [] }));
        }
    };

 
    const toggleAllProjectsForUser = (userId) => {
        const curr = userProjectsMap[userId] || [];
        if (curr.length < projects.length) {
            setUserProjectsMap((prev) => ({ ...prev, [userId]: projects.map((p) => p.id) }));
        } else {
            setUserProjectsMap((prev) => ({ ...prev, [userId]: [] }));
        }
    };

    const handleSaveAll = () => {
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        };

        const postProjects = fetch(`${process.env.REACT_APP_API_URL}/user-projects`, {
            method: "POST",
            headers,
            body: JSON.stringify(userProjectsMap),
        }).then((res) => {
            if (!res.ok) throw new Error(`POST /user-projects failed (${res.status})`);
            return res.json();
        });

        const postRoles = fetch(`${process.env.REACT_APP_API_URL}/user-roles`, {
            method: "POST",
            headers,
            body: JSON.stringify(userRolesMap),
        }).then((res) => {
            if (!res.ok) throw new Error(`POST /user-roles failed (${res.status})`);
            return res.json();
        });

        Promise.all([postProjects, postRoles])
            .then(() => {
                alert("All changes saved successfully!");
            })
            .catch((err) => {
                console.error("Error saving mappings:", err);
                alert("Failed to save. See console for details.");
            });
    };

 
    // 9) Create a new user via POST /register, then re-fetch mappings
 
    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (!newName || !newEmail || !newPassword) {
            alert("Please fill in all fields.");
            return;
        }
        setCreatingUser(true);

        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/register`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    name: newName.trim(),
                    email: newEmail.trim(),
                    password: newPassword,
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.message || `HTTP ${res.status}`);
            }

            // On success: clear form, hide it, and re-fetch users/ mappings
            setNewName("");
            setNewEmail("");
            setNewPassword("");
            setShowAddForm(false);

            // Re-fetch everything quickly:
            setLoading(true);
            const headers = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            };
            const [newUsers, newUserRoles, newUserProjects] = await Promise.all([
                fetch(`${process.env.REACT_APP_API_URL}/users`, { headers }).then((r) => {
                    if (!r.ok) throw new Error(`GET /users failed`);
                    return r.json();
                }),
                fetch(`${process.env.REACT_APP_API_URL}/user-roles`, { headers }).then((r) => {
                    if (!r.ok) throw new Error(`GET /user-roles failed`);
                    return r.json();
                }),
                fetch(`${process.env.REACT_APP_API_URL}/user-projects`, { headers }).then((r) => {
                    if (!r.ok) throw new Error(`GET /user-projects failed`);
                    return r.json();
                }),
            ]);

            setUsers(Array.isArray(newUsers) ? newUsers : []);
            setUserRolesMap(
                newUserRoles && typeof newUserRoles === "object" && !Array.isArray(newUserRoles)
                    ? newUserRoles
                    : {}
            );
            setUserProjectsMap(
                newUserProjects && typeof newUserProjects === "object" && !Array.isArray(newUserProjects)
                    ? newUserProjects
                    : {}
            );
        } catch (err) {
            console.error("Error creating user:", err);
            alert("Failed to create user. Check console.");
        } finally {
            setCreatingUser(false);
            setLoading(false);
        }
    };

 
    // 10) Render
 
    return (
        <div className="px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12">
         
            {/* A) Top “Tabs” to choose between AddUser / ProjectAccess / Roles */}
         
            <div className="flex space-x-4 mb-6">
                <button
                    onClick={() => {
                        setActiveView("addUser");
                        setShowAddForm(true);
                    }}
                    className={`px-4 py-2 rounded-md font-medium focus:outline-none focus:ring-2 ${
                        activeView === "addUser"
                            ? "bg-indigo-600 text-white ring-indigo-400"
                            : "bg-gray-200 text-gray-700 ring-gray-300 hover:bg-gray-300"
                    }`}
                >
                    Add New User
                </button>
                <button
                    onClick={() => {
                        setActiveView("projects");
                        setShowAddForm(false);
                    }}
                    className={`px-4 py-2 rounded-md font-medium focus:outline-none focus:ring-2 ${
                        activeView === "projects"
                            ? "bg-indigo-600 text-white ring-indigo-400"
                            : "bg-gray-200 text-gray-700 ring-gray-300 hover:bg-gray-300"
                    }`}
                >
                    Project Access
                </button>
                <button
                    onClick={() => {
                        setActiveView("roles");
                        setShowAddForm(false);
                    }}
                    className={`px-4 py-2 rounded-md font-medium focus:outline-none focus:ring-2 ${
                        activeView === "roles"
                            ? "bg-indigo-600 text-white ring-indigo-400"
                            : "bg-gray-200 text-gray-700 ring-gray-300 hover:bg-gray-300"
                    }`}
                >
                    Roles
                </button>
            </div>

         
            {/* B) If activeView === "addUser": show the “Add New User” form */}
         
            {activeView === "addUser" && (
                <div className="bg-white rounded-lg border border-gray-200 shadow-md mb-6">
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                        <h2 className="text-2xl font-bold">Add New User</h2>
                        <button
                            onClick={() => setShowAddForm((prev) => !prev)}
                            className="
                bg-indigo-600 hover:bg-indigo-700 text-white font-medium
                px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-400
              "
                        >
                            {showAddForm ? "Hide Form" : "Show Form"}
                        </button>
                    </div>

                    {showAddForm && (
                        <div className="px-6 py-4">
                            <form onSubmit={handleCreateUser} className="space-y-4">
                                <div>
                                    <label htmlFor="newName" className="block text-sm font-medium text-gray-700">
                                        Name
                                    </label>
                                    <input
                                        id="newName"
                                        type="text"
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        placeholder="e.g. Jane Doe"
                                        required
                                        className="
                      mt-1 block w-full border border-gray-300 rounded-md
                      shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500
                    "
                                    />
                                </div>

                                <div>
                                    <label htmlFor="newEmail" className="block text-sm font-medium text-gray-700">
                                        Email
                                    </label>
                                    <input
                                        id="newEmail"
                                        type="email"
                                        value={newEmail}
                                        onChange={(e) => setNewEmail(e.target.value)}
                                        placeholder="e.g. jane@example.com"
                                        required
                                        className="
                      mt-1 block w-full border border-gray-300 rounded-md
                      shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500
                    "
                                    />
                                </div>

                                <div>
                                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                                        Password
                                    </label>
                                    <input
                                        id="newPassword"
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="At least 8 characters"
                                        minLength={8}
                                        required
                                        className="
                      mt-1 block w-full border border-gray-300 rounded-md
                      shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500
                    "
                                    />
                                </div>

                                <div className="pt-2">
                                    <button
                                        type="submit"
                                        disabled={creatingUser}
                                        className={`${
                                            creatingUser
                                                ? "bg-gray-400 cursor-not-allowed"
                                                : "bg-indigo-600 hover:bg-indigo-700"
                                        } 
                      text-white font-medium px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-400
                    `}
                                    >
                                        {creatingUser ? "Creating…" : "Create User"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            )}

         
            {/* C) If activeView === "projects": show USER→PROJECT table */}
         
            {activeView === "projects" && (
                <div className="bg-white rounded-lg border border-gray-200 shadow-md mb-6">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-2xl font-bold">User → Project Access</h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Assign or revoke project‐level access for each user.
                        </p>
                    </div>

                    <div className="overflow-x-auto p-6">
                        <table className="min-w-full table-auto border-collapse">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">
                                    User
                                </th>
                                {projects.map((p) => (
                                    <th
                                        key={`proj-header-${p.id}`}
                                        className="px-4 py-2 text-center text-sm font-medium text-gray-700 border-b"
                                    >
                                        {p.name}
                                    </th>
                                ))}
                                <th className="px-4 py-2 text-center text-sm font-medium text-gray-700 border-b">
                                    Actions
                                </th>
                            </tr>
                            </thead>

                            <tbody className="bg-white divide-y divide-gray-200">
                            {users.map((u) => {
                                const userId = u.id;
                                const thisUserProjects = userProjectsMap[userId] || [];

                                return (
                                    <tr key={`proj-row-${userId}`}>
                                        {/* User cell */}
                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-800 align-middle">
                                            {u.name} (ID: {userId})
                                        </td>

                                        {/* Project checkboxes */}
                                        {projects.map((p) => {
                                            const isChecked = thisUserProjects.includes(p.id);
                                            return (
                                                <td
                                                    key={`proj-cell-${userId}-${p.id}`}
                                                    className="px-4 py-3 text-center whitespace-nowrap align-middle"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={() => toggleProjectForUser(userId, p.id)}
                                                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                                        aria-label={`${
                                                            isChecked ? "Revoke" : "Grant"
                                                        } access to project ${p.name} for user ${u.name}`}
                                                    />
                                                </td>
                                            );
                                        })}

                                        {/* Actions: All/None for projects */}
                                        <td className="px-4 py-3 text-center whitespace-nowrap align-middle">
                                            <button
                                                type="button"
                                                onClick={() => toggleAllProjectsForUser(userId)}
                                                className="
                            text-indigo-600 hover:text-indigo-800 text-sm font-medium
                            focus:outline-none focus:ring-2 focus:ring-indigo-400 rounded px-2 py-1
                          "
                                                aria-label={
                                                    thisUserProjects.length < projects.length
                                                        ? `Select all projects for ${u.name}`
                                                        : `Deselect all projects for ${u.name}`
                                                }
                                            >
                                                {thisUserProjects.length < projects.length ? "All Projs" : "No Projs"}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>

                    {/* Save button */}
                    <div className="sticky bottom-0 bg-white py-4 flex justify-end border-t border-gray-200 mt-6 px-6">
                        <button
                            onClick={handleSaveAll}
                            className="
                bg-indigo-600 hover:bg-indigo-700 text-white font-medium
                px-6 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400
              "
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            )}

         
            {/* D) If activeView === "roles": show USER→ROLE table */}
         
            {activeView === "roles" && (
                <div className="bg-white rounded-lg border border-gray-200 shadow-md mb-6">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-2xl font-bold">User → Role Management</h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Assign or revoke role‐level permissions for each user.
                        </p>
                    </div>

                    <div className="overflow-x-auto p-6">
                        <table className="min-w-full table-auto border-collapse">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 border-b">
                                    User
                                </th>
                                {roles.map((r) => (
                                    <th
                                        key={`role-header-${r.id}`}
                                        className="px-4 py-2 text-center text-sm font-medium text-gray-700 border-b"
                                    >
                                        {r.name}
                                    </th>
                                ))}
                                <th className="px-4 py-2 text-center text-sm font-medium text-gray-700 border-b">
                                    Actions
                                </th>
                            </tr>
                            </thead>

                            <tbody className="bg-white divide-y divide-gray-200">
                            {users.map((u) => {
                                const userId = u.id;
                                const thisUserRoles = userRolesMap[userId] || [];

                                return (
                                    <tr key={`role-row-${userId}`}>
                                        {/* User cell */}
                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-800 align-middle">
                                            {u.name} (ID: {userId})
                                        </td>

                                        {/* Role checkboxes */}
                                        {roles.map((r) => {
                                            const isChecked = thisUserRoles.includes(r.id);
                                            return (
                                                <td
                                                    key={`role-cell-${userId}-${r.id}`}
                                                    className="px-4 py-3 text-center whitespace-nowrap align-middle"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={() => toggleRoleForUser(userId, r.id)}
                                                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                                        aria-label={`${
                                                            isChecked ? "Revoke" : "Assign"
                                                        } role ${r.name} for user ${u.name}`}
                                                    />
                                                </td>
                                            );
                                        })}

                                        {/* Actions: All/None for roles */}
                                        <td className="px-4 py-3 text-center whitespace-nowrap align-middle">
                                            <button
                                                type="button"
                                                onClick={() => toggleAllRolesForUser(userId)}
                                                className="
                            text-indigo-600 hover:text-indigo-800 text-sm font-medium
                            focus:outline-none focus:ring-2 focus:ring-indigo-400 rounded px-2 py-1
                          "
                                                aria-label={
                                                    thisUserRoles.length < roles.length
                                                        ? `Select all roles for ${u.name}`
                                                        : `Deselect all roles for ${u.name}`
                                                }
                                            >
                                                {thisUserRoles.length < roles.length ? "All Roles" : "No Roles"}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>

                    {/* Save button */}
                    <div className="sticky bottom-0 bg-white py-4 flex justify-end border-t border-gray-200 mt-6 px-6">
                        <button
                            onClick={handleSaveAll}
                            className="
                bg-indigo-600 hover:bg-indigo-700 text-white font-medium
                px-6 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400
              "
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserRestrictionsManagement;
